import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import {
  ArrowLeft, Truck, ShoppingCart, Plus, Trash2, CheckCircle, XCircle,
  PackageCheck, Clock, Package, Filter, Calendar, Users, HardHat, Archive, LayoutList,
  Briefcase, PenTool
} from 'lucide-react';
import type { DiversSubProject } from '../types';

// TYPES
interface Supplier {
  id: string;
  name: string;
  contact_name: string;
  phone: string;
  category: string;
}

interface MaterialRequest {
  id: string;
  created_at: string;
  description: string;
  urgency: string;
  status: string;
  target: string; // 'direction' | 'be'
  employee_id: string; // Ajout pour le filtre
  project_id: string;  // Ajout pour le filtre
  divers_sub_project_id?: string | null;
  employee: { full_name: string };
  project: { code: string; name: string; category?: 'production' | 'divers' };
  supplier_id?: string;
}

// Helper pour obtenir le 1er du mois courant
const getFirstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

// Helper pour obtenir aujourd'hui
const getToday = () => new Date().toISOString().split('T')[0];

interface AdminMaterialsProps {
  defaultTarget?: 'direction' | 'be' | 'all';
}

export default function AdminMaterials({ defaultTarget = 'direction' }: AdminMaterialsProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'orders' | 'suppliers'>('orders');
  const [loading, setLoading] = useState(true);

  // DATA
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [requests, setRequests] = useState<MaterialRequest[]>([]);
  const [diversSubProjects, setDiversSubProjects] = useState<DiversSubProject[]>([]);

  // FILTRES - JLO par défaut pour Admin, dates du mois courant
  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterEmployee, setFilterEmployee] = useState<string>('all');
  const [filterTarget, setFilterTarget] = useState<string>(defaultTarget);
  const [dateStart, setDateStart] = useState<string>(getFirstDayOfMonth());
  const [dateEnd, setDateEnd] = useState<string>(getToday());

  // FORMULAIRE FOURNISSEUR
  const [newSupplier, setNewSupplier] = useState({ name: '', contact_name: '', phone: '', category: '' });

  // 1. Définition stable de fetchData avec useCallback
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fournisseurs
      const { data: supps } = await supabase
        .from('suppliers')
        .select('*')
        .order('name');
      if (supps) setSuppliers(supps);

      // 2. Commandes (avec catégorie et divers_sub_project_id)
      const { data: reqs } = await supabase
        .from('material_requests')
        .select('*, employee:employees(full_name), project:projects(code, name, category), divers_sub_project_id')
        .order('created_at', { ascending: false });
      if (reqs) setRequests(reqs as unknown as MaterialRequest[]);

      // 3. Sous-chantiers Divers (pour affichage des noms)
      const { data: subProjs } = await supabase
        .from('divers_sub_projects')
        .select('*')
        .order('divers_number');
      if (subProjs) setDiversSubProjects(subProjs as DiversSubProject[]);
    } catch (error) {
      console.error("Erreur chargement données:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- LOGIQUE DE FILTRAGE ---
  
  // 1. Extraire les listes uniques pour les dropdowns
  const uniqueProjects = useMemo(() => {
    const map = new Map();
    requests.forEach(r => {
      if (r.project) map.set(r.project_id, `${r.project.code} - ${r.project.name}`);
    });
    return Array.from(map.entries());
  }, [requests]);

  const uniqueEmployees = useMemo(() => {
    const map = new Map();
    requests.forEach(r => {
      if (r.employee) map.set(r.employee_id, r.employee.full_name);
    });
    return Array.from(map.entries());
  }, [requests]);

  // 2. Appliquer les filtres
  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      // A. Filtre Statut (Vue Active vs Archives)
      const isActive = req.status === 'pending' || req.status === 'validated';
      if (viewMode === 'active' && !isActive) return false;
      if (viewMode === 'archived' && isActive) return false;

      // B. Filtre Projet
      if (filterProject !== 'all' && req.project_id !== filterProject) return false;

      // C. Filtre Employé
      if (filterEmployee !== 'all' && req.employee_id !== filterEmployee) return false;

      // D. Filtre Destinataire
      if (filterTarget !== 'all' && req.target !== filterTarget) return false;

      // E. Filtre Date
      const reqDate = req.created_at.split('T')[0];
      if (dateStart && reqDate < dateStart) return false;
      if (dateEnd && reqDate > dateEnd) return false;

      return true;
    });
  }, [requests, viewMode, filterProject, filterEmployee, filterTarget, dateStart, dateEnd]);


  // --- LOGIQUE FOURNISSEURS ---
  const addSupplier = async () => {
    if (!newSupplier.name) return;
    await supabase.from('suppliers').insert(newSupplier);
    setNewSupplier({ name: '', contact_name: '', phone: '', category: '' });
    fetchData();
  };

  const deleteSupplier = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Supprimer ce fournisseur ?")) return;
    await supabase.from('suppliers').delete().eq('id', id);
    fetchData();
  };

  // --- LOGIQUE COMMANDES ---
  const updateStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('material_requests')
        .update({ status })
        .eq('id', id);
      if (error) {
        console.error('[AdminMaterials] updateStatus error:', error);
        toast.error(`Erreur: ${error.message}`);
        return;
      }
      await fetchData();
    } catch (err: unknown) {
      console.error('[AdminMaterials] updateStatus unexpected error:', err);
      toast.error("Une erreur inattendue est survenue.");
    }
  };

  // Helper pour les badges de statut
  const getStatusBadge = (status: string) => {
    const baseStyle = { 
      padding: '8px 16px', // Plus grand
      borderRadius: '30px', 
      fontSize: '0.8rem', 
      fontWeight: '700',
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '8px',
      letterSpacing: '0.05em',
      textTransform: 'uppercase' as const,
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    };

    switch (status) {
      case 'pending': return <span style={{ ...baseStyle, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d' }}><Clock size={16} /> En attente</span>;
      case 'validated': return <span style={{ ...baseStyle, color: '#15803d', background: '#dcfce7', border: '1px solid #86efac' }}><Package size={16} /> Commandé</span>;
      case 'received': return <span style={{ ...baseStyle, color: '#1e40af', background: '#dbeafe', border: '1px solid #93c5fd' }}><CheckCircle size={16} /> Reçu</span>;
      case 'rejected': return <span style={{ ...baseStyle, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5' }}><XCircle size={16} /> Refusé</span>;
      default: return null;
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', paddingBottom: '80px', background: '#f8fafc', minHeight: '100vh' }}>

      {/* HEADER MODERNE */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        padding: '24px 30px',
        marginBottom: '30px',
        borderRadius: '0 0 24px 24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              padding: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <ArrowLeft size={20} color="white" />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', color: 'white', fontWeight: '700' }}>Gestion Matériel</h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
              Commandes et fournisseurs
            </p>
          </div>
        </div>

        {/* TABS INTÉGRÉS AU HEADER */}
        <div style={{ display:'flex', gap:'10px', marginTop:'20px' }}>
          <button
            onClick={() => setActiveTab('orders')}
            style={{
              padding:'12px 24px',
              borderRadius:'12px',
              background: activeTab==='orders' ? 'white' : 'rgba(255,255,255,0.1)',
              color: activeTab==='orders' ? '#1e293b' : 'rgba(255,255,255,0.8)',
              cursor:'pointer',
              fontWeight:'600',
              display:'flex',
              alignItems:'center',
              gap:'10px',
              boxShadow: activeTab==='orders' ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
              border: activeTab==='orders' ? 'none' : '1px solid rgba(255,255,255,0.2)',
              fontSize: '0.95rem',
              transition: 'all 0.25s ease'
            }}
          >
            <ShoppingCart size={18} /> Suivi Commandes
          </button>
          <button
            onClick={() => setActiveTab('suppliers')}
            style={{
              padding:'12px 24px',
              borderRadius:'12px',
              background: activeTab==='suppliers' ? 'white' : 'rgba(255,255,255,0.1)',
              color: activeTab==='suppliers' ? '#1e293b' : 'rgba(255,255,255,0.8)',
              cursor:'pointer',
              fontWeight:'600',
              display:'flex',
              alignItems:'center',
              gap:'10px',
              boxShadow: activeTab==='suppliers' ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
              border: activeTab==='suppliers' ? 'none' : '1px solid rgba(255,255,255,0.2)',
              fontSize: '0.95rem',
              transition: 'all 0.25s ease'
            }}
          >
            <Truck size={18} /> Fournisseurs
          </button>
        </div>
      </div>

      {/* CONTENU PRINCIPAL */}
      <div style={{ padding: '0 20px' }}>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
          <p>Chargement des données...</p>
        </div>
      ) : (
        <>
          {/* ONGLET COMMANDES */}
          {activeTab === 'orders' && (
            <div style={{ maxWidth: '1000px', display:'flex', flexDirection:'column', gap:'24px' }}>
              
              {/* BARRE DE FILTRES MODERNISÉE */}
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
                display: 'flex',
                flexDirection:'column',
                gap: '20px'
              }}>

                {/* Ligne 1 : Toggle Active / Archives + Compteur */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ display:'flex', gap:'6px', background:'linear-gradient(145deg, #f1f5f9, #e2e8f0)', padding:'5px', borderRadius:'14px' }}>
                    <button
                      onClick={() => setViewMode('active')}
                      style={{
                        padding:'10px 20px', borderRadius:'10px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.9rem',
                        background: viewMode === 'active' ? 'white' : 'transparent',
                        color: viewMode === 'active' ? '#2563eb' : '#64748b',
                        boxShadow: viewMode === 'active' ? '0 2px 8px rgba(37,99,235,0.15)' : 'none',
                        display:'flex', alignItems:'center', gap:'8px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <LayoutList size={16} /> En cours
                    </button>
                    <button
                      onClick={() => setViewMode('archived')}
                      style={{
                        padding:'10px 20px', borderRadius:'10px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.9rem',
                        background: viewMode === 'archived' ? 'white' : 'transparent',
                        color: viewMode === 'archived' ? '#475569' : '#64748b',
                        boxShadow: viewMode === 'archived' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                        display:'flex', alignItems:'center', gap:'8px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Archive size={16} /> Historique
                    </button>
                  </div>
                  <div style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    padding: '10px 18px',
                    borderRadius: '30px',
                    fontSize:'0.9rem',
                    color:'#2563eb',
                    fontWeight:'700',
                    display:'flex',
                    alignItems:'center',
                    gap:'6px'
                  }}>
                    <Package size={16} />
                    {filteredRequests.length} demande{filteredRequests.length > 1 ? 's' : ''}
                  </div>
                </div>

                {/* Séparateur */}
                <div style={{ height:'1px', background:'linear-gradient(90deg, transparent, #e2e8f0, transparent)' }} />

                {/* Ligne 2 : Filtres */}
                <div style={{ display:'flex', gap:'16px', flexWrap:'wrap', alignItems:'center' }}>
                  <div style={{
                    display:'flex',
                    alignItems:'center',
                    gap:'8px',
                    color:'#475569',
                    fontWeight:'600',
                    fontSize:'0.85rem',
                    background:'#f8fafc',
                    padding:'8px 14px',
                    borderRadius:'10px'
                  }}>
                    <Filter size={16} color="#2563eb" /> Filtres
                  </div>

                  {/* Projet */}
                  <div style={{ position:'relative' }}>
                    <HardHat size={15} style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', color:'#64748b' }} />
                    <select
                      value={filterProject}
                      onChange={(e) => setFilterProject(e.target.value)}
                      style={{
                        padding:'10px 14px 10px 36px',
                        borderRadius:'10px',
                        border:'1px solid #e2e8f0',
                        fontSize:'0.9rem',
                        background:'white',
                        minWidth:'180px',
                        cursor:'pointer',
                        fontWeight:'500',
                        color:'#334155',
                        transition:'all 0.2s',
                        outline:'none'
                      }}
                    >
                      <option value="all">Tous les chantiers</option>
                      {uniqueProjects.map(([id, name]) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Employé */}
                  <div style={{ position:'relative' }}>
                    <Users size={15} style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', color:'#64748b' }} />
                    <select
                      value={filterEmployee}
                      onChange={(e) => setFilterEmployee(e.target.value)}
                      style={{
                        padding:'10px 14px 10px 36px',
                        borderRadius:'10px',
                        border:'1px solid #e2e8f0',
                        fontSize:'0.9rem',
                        background:'white',
                        minWidth:'180px',
                        cursor:'pointer',
                        fontWeight:'500',
                        color:'#334155',
                        transition:'all 0.2s',
                        outline:'none'
                      }}
                    >
                      <option value="all">Tous les employés</option>
                      {uniqueEmployees.map(([id, name]) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Destinataire - Style amélioré */}
                  <div style={{
                    display:'flex',
                    background:'linear-gradient(145deg, #f8fafc, #f1f5f9)',
                    padding:'4px',
                    borderRadius:'12px',
                    gap:'4px',
                    border:'1px solid #e2e8f0'
                  }}>
                    <button
                      onClick={() => setFilterTarget('all')}
                      style={{
                        padding:'8px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem',
                        background: filterTarget === 'all' ? 'white' : 'transparent',
                        color: filterTarget === 'all' ? '#334155' : '#94a3b8',
                        boxShadow: filterTarget === 'all' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                        transition:'all 0.2s ease'
                      }}
                    >
                      Tous
                    </button>
                    <button
                      onClick={() => setFilterTarget('direction')}
                      style={{
                        padding:'8px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem',
                        background: filterTarget === 'direction' ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' : 'transparent',
                        color: filterTarget === 'direction' ? '#b45309' : '#94a3b8',
                        boxShadow: filterTarget === 'direction' ? '0 2px 6px rgba(180,83,9,0.12)' : 'none',
                        display:'flex', alignItems:'center', gap:'6px',
                        transition:'all 0.2s ease'
                      }}
                    >
                      <Briefcase size={14} /> JLO
                    </button>
                    <button
                      onClick={() => setFilterTarget('be')}
                      style={{
                        padding:'8px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem',
                        background: filterTarget === 'be' ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' : 'transparent',
                        color: filterTarget === 'be' ? '#6d28d9' : '#94a3b8',
                        boxShadow: filterTarget === 'be' ? '0 2px 6px rgba(109,40,217,0.12)' : 'none',
                        display:'flex', alignItems:'center', gap:'6px',
                        transition:'all 0.2s ease'
                      }}
                    >
                      <PenTool size={14} /> GP
                    </button>
                  </div>
                </div>

                {/* Ligne 3 : Dates */}
                <div style={{ display:'flex', gap:'12px', flexWrap:'wrap', alignItems:'center' }}>
                  <span style={{ fontSize:'0.85rem', color:'#64748b', fontWeight:'500' }}>Période :</span>
                  <div style={{
                    display:'flex',
                    background:'linear-gradient(145deg, #f8fafc, #f1f5f9)',
                    padding:'4px',
                    borderRadius:'12px',
                    gap:'4px',
                    border:'1px solid #e2e8f0'
                  }}>
                    <button
                      onClick={() => { setDateStart(getFirstDayOfMonth()); setDateEnd(getToday()); }}
                      style={{
                        padding:'8px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem',
                        background: dateStart === getFirstDayOfMonth() && dateEnd === getToday() ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : 'transparent',
                        color: dateStart === getFirstDayOfMonth() && dateEnd === getToday() ? '#2563eb' : '#94a3b8',
                        boxShadow: dateStart === getFirstDayOfMonth() && dateEnd === getToday() ? '0 2px 6px rgba(37,99,235,0.12)' : 'none',
                        transition:'all 0.2s ease'
                      }}
                    >
                      Ce mois
                    </button>
                    <button
                      onClick={() => { setDateStart(''); setDateEnd(''); }}
                      style={{
                        padding:'8px 14px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem',
                        background: !dateStart && !dateEnd ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : 'transparent',
                        color: !dateStart && !dateEnd ? '#2563eb' : '#94a3b8',
                        boxShadow: !dateStart && !dateEnd ? '0 2px 6px rgba(37,99,235,0.12)' : 'none',
                        transition:'all 0.2s ease'
                      }}
                    >
                      Tout
                    </button>
                  </div>
                  <div style={{
                    display:'flex',
                    alignItems:'center',
                    gap:'10px',
                    background:'white',
                    padding:'8px 16px',
                    borderRadius:'12px',
                    border:'1px solid #e2e8f0',
                    boxShadow:'inset 0 1px 2px rgba(0,0,0,0.03)'
                  }}>
                    <Calendar size={16} color="#2563eb" />
                    <input
                      type="date"
                      value={dateStart}
                      onChange={e => setDateStart(e.target.value)}
                      style={{ border:'none', background:'transparent', fontSize:'0.9rem', color:'#334155', cursor:'pointer', fontWeight:'500', outline:'none' }}
                    />
                    <span style={{ color:'#cbd5e1', fontSize:'1.2rem' }}>→</span>
                    <input
                      type="date"
                      value={dateEnd}
                      onChange={e => setDateEnd(e.target.value)}
                      style={{ border:'none', background:'transparent', fontSize:'0.9rem', color:'#334155', cursor:'pointer', fontWeight:'500', outline:'none' }}
                    />
                  </div>

                  {(filterProject !== 'all' || filterEmployee !== 'all' || filterTarget !== defaultTarget) && (
                    <button
                      onClick={() => { setFilterProject('all'); setFilterEmployee('all'); setFilterTarget(defaultTarget); setDateStart(getFirstDayOfMonth()); setDateEnd(getToday()); }}
                      style={{
                        fontSize:'0.85rem',
                        color:'#ef4444',
                        background:'#fef2f2',
                        border:'1px solid #fecaca',
                        borderRadius:'8px',
                        padding:'8px 14px',
                        cursor:'pointer',
                        fontWeight:'600',
                        transition:'all 0.2s ease'
                      }}
                    >
                      Réinitialiser
                    </button>
                  )}
                </div>
              </div>
              
              {/* LISTE DES RÉSULTATS */}
              {filteredRequests.length === 0 && (
                <div style={{
                  textAlign:'center',
                  padding:'80px 40px',
                  background:'linear-gradient(145deg, #ffffff, #f8fafc)',
                  borderRadius:'24px',
                  border:'2px dashed #e2e8f0',
                  color:'#94a3b8'
                }}>
                  <div style={{
                    width:'80px',
                    height:'80px',
                    background:'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                    borderRadius:'50%',
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    margin:'0 auto 20px'
                  }}>
                    <ShoppingCart size={36} color="#94a3b8" />
                  </div>
                  <p style={{ fontSize:'1.1rem', fontWeight:'500', margin:0 }}>Aucune commande ne correspond aux filtres</p>
                  <p style={{ fontSize:'0.9rem', marginTop:'8px', color:'#cbd5e1' }}>Essayez de modifier vos critères de recherche</p>
                </div>
              )}

              {filteredRequests.map(req => {
                const isUrgent = req.urgency === 'high';

                // Déterminer le nom à afficher (sous-chantier Divers si applicable)
                let displayName = req.project?.name || '';
                if (req.divers_sub_project_id && req.project?.category === 'divers') {
                  const subProj = diversSubProjects.find(sp => sp.id === req.divers_sub_project_id);
                  if (subProj) {
                    displayName = subProj.name;
                  }
                }

                return (
                  <div
                    key={req.id}
                    style={{
                      background: 'white',
                      borderRadius: '20px',
                      border: isUrgent ? '2px solid #fecaca' : '1px solid #e2e8f0',
                      overflow:'hidden',
                      boxShadow: isUrgent
                        ? '0 10px 25px -5px rgba(239,68,68,0.1), 0 4px 6px -2px rgba(239,68,68,0.05)'
                        : '0 10px 25px -5px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03)',
                      transition: 'all 0.3s ease',
                      position:'relative' as const
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isUrgent
                        ? '0 20px 35px -10px rgba(239,68,68,0.15), 0 8px 12px -4px rgba(239,68,68,0.08)'
                        : '0 20px 35px -10px rgba(0,0,0,0.1), 0 8px 12px -4px rgba(0,0,0,0.05)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = isUrgent
                        ? '0 10px 25px -5px rgba(239,68,68,0.1), 0 4px 6px -2px rgba(239,68,68,0.05)'
                        : '0 10px 25px -5px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03)';
                    }}
                  >
                    {/* Bande latérale urgence */}
                    {isUrgent && (
                      <div style={{
                        position:'absolute',
                        left:0,
                        top:0,
                        bottom:0,
                        width:'5px',
                        background:'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)'
                      }} />
                    )}

                    {/* EN-TÊTE CARTE */}
                    <div style={{
                      padding: '24px 30px',
                      background: isUrgent ? 'linear-gradient(135deg, #fff5f5 0%, #fef2f2 100%)' : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '15px'
                    }}>
                      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                          <span style={{
                            fontWeight: '700',
                            fontSize: '1.1rem',
                            color: '#1e293b'
                          }}>
                            {displayName}
                          </span>
                          {/* Badge Destinataire */}
                          {req.target === 'be' ? (
                            <span style={{
                              background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                              color: '#6d28d9',
                              fontSize: '0.8rem',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontWeight:'700',
                              display:'flex',
                              alignItems:'center',
                              gap:'5px',
                              border: '1px solid #ddd6fe',
                              boxShadow:'0 2px 4px rgba(109,40,217,0.1)'
                            }}>
                              <PenTool size={13} /> GP
                            </span>
                          ) : (
                            <span style={{
                              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                              color: '#b45309',
                              fontSize: '0.8rem',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontWeight:'700',
                              display:'flex',
                              alignItems:'center',
                              gap:'5px',
                              border: '1px solid #fcd34d',
                              boxShadow:'0 2px 4px rgba(180,83,9,0.1)'
                            }}>
                              <Briefcase size={13} /> JLO
                            </span>
                          )}
                          {isUrgent && (
                            <span style={{
                              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: 'white',
                              fontSize: '0.75rem',
                              padding: '6px 14px',
                              borderRadius: '20px',
                              fontWeight:'700',
                              letterSpacing:'0.08em',
                              display:'flex',
                              alignItems:'center',
                              gap:'5px',
                              boxShadow:'0 4px 8px rgba(239,68,68,0.25)',
                              textTransform:'uppercase' as const
                            }}>
                              <Clock size={13} /> Urgent
                            </span>
                          )}
                        </div>
                        <div style={{
                          fontSize: '0.9rem',
                          color: '#64748b',
                          display:'flex',
                          alignItems:'center',
                          gap:'8px',
                          marginTop:'4px'
                        }}>
                          <Users size={14} color="#94a3b8" />
                          Demandé par <strong style={{color:'#334155'}}>{req.employee?.full_name}</strong>
                          <span style={{color:'#cbd5e1'}}>•</span>
                          <Calendar size={14} color="#94a3b8" />
                          {new Date(req.created_at).toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long' })}
                        </div>
                      </div>

                      {/* Badge Statut */}
                      <div>
                        {getStatusBadge(req.status)}
                      </div>
                    </div>

                    {/* CORPS CARTE */}
                    <div style={{ padding: '28px 30px' }}>
                      <div style={{
                        background: 'linear-gradient(145deg, #f8fafc, #f1f5f9)',
                        padding: '22px 24px',
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        color:'#334155',
                        lineHeight:'1.7',
                        fontSize: '1rem',
                        whiteSpace: 'pre-wrap',
                        boxShadow:'inset 0 2px 4px rgba(0,0,0,0.02)'
                      }}>
                        {req.description}
                      </div>

                      {/* ACTIONS */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateStatus(req.id, 'rejected')}
                              style={{
                                padding: '14px 22px',
                                background: 'linear-gradient(145deg, #ffffff, #fef2f2)',
                                color: '#b91c1c',
                                border: '1px solid #fecaca',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontWeight:'600',
                                display:'flex',
                                alignItems:'center',
                                gap:'8px',
                                transition:'all 0.2s ease',
                                fontSize:'0.95rem'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = '#fef2f2';
                                e.currentTarget.style.transform = 'scale(1.02)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(145deg, #ffffff, #fef2f2)';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              <XCircle size={20} /> Refuser
                            </button>
                            <button
                              onClick={() => updateStatus(req.id, 'validated')}
                              style={{
                                padding: '14px 26px',
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontWeight:'600',
                                display: 'flex',
                                alignItems: 'center',
                                gap:'8px',
                                boxShadow:'0 6px 14px rgba(37,99,235,0.3)',
                                transition:'all 0.2s ease',
                                fontSize:'0.95rem'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'scale(1.03)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(37,99,235,0.4)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = '0 6px 14px rgba(37,99,235,0.3)';
                              }}
                            >
                              <CheckCircle size={20} /> Valider & Commander
                            </button>
                          </>
                        )}
                        {req.status === 'validated' && (
                          <button
                            onClick={() => updateStatus(req.id, 'received')}
                            style={{
                              padding: '14px 26px',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              fontWeight:'600',
                              display: 'flex',
                              alignItems: 'center',
                              gap:'8px',
                              boxShadow:'0 6px 14px rgba(16,185,129,0.3)',
                              transition:'all 0.2s ease',
                              fontSize:'0.95rem'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'scale(1.03)';
                              e.currentTarget.style.boxShadow = '0 8px 20px rgba(16,185,129,0.4)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = '0 6px 14px rgba(16,185,129,0.3)';
                            }}
                          >
                            <PackageCheck size={20} /> Confirmer Réception
                          </button>
                        )}
                        {(req.status === 'received' || req.status === 'rejected') && (
                          <div style={{
                            fontSize:'0.9rem',
                            color:'#64748b',
                            fontWeight:'500',
                            padding:'12px 24px',
                            background:'linear-gradient(145deg, #f8fafc, #f1f5f9)',
                            borderRadius:'30px',
                            display:'flex',
                            alignItems:'center',
                            gap:'8px'
                          }}>
                            <CheckCircle size={16} color="#94a3b8" />
                            Dossier clôturé
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ONGLET FOURNISSEURS */}
          {activeTab === 'suppliers' && (
            <div style={{ maxWidth: '1000px' }}>
              {/* Formulaire Ajout Modernisé */}
              <div style={{
                background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
                padding: '32px',
                borderRadius: '24px',
                border: '1px solid #e2e8f0',
                marginBottom: '30px',
                boxShadow:'0 10px 25px -5px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03)'
              }}>
                <h3 style={{
                  margin: '0 0 24px 0',
                  fontSize: '1.25rem',
                  color: '#1e293b',
                  display:'flex',
                  alignItems:'center',
                  gap:'12px'
                }}>
                  <div style={{
                    background:'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    padding:'12px',
                    borderRadius:'14px',
                    color:'#2563eb',
                    boxShadow:'0 4px 8px rgba(37,99,235,0.15)'
                  }}>
                    <Plus size={22} />
                  </div>
                  Nouveau Fournisseur
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{
                      display:'block',
                      fontSize:'0.85rem',
                      fontWeight:'600',
                      color:'#475569',
                      marginBottom:'8px'
                    }}>
                      Nom Entreprise
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Point P"
                      value={newSupplier.name}
                      onChange={e => setNewSupplier({...newSupplier, name: e.target.value})}
                      style={{
                        width:'100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        fontSize:'1rem',
                        transition:'all 0.2s',
                        outline:'none',
                        background:'white'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display:'block',
                      fontSize:'0.85rem',
                      fontWeight:'600',
                      color:'#475569',
                      marginBottom:'8px'
                    }}>
                      Contact
                    </label>
                    <input
                      type="text"
                      placeholder="Nom du commercial"
                      value={newSupplier.contact_name}
                      onChange={e => setNewSupplier({...newSupplier, contact_name: e.target.value})}
                      style={{
                        width:'100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        fontSize:'1rem',
                        transition:'all 0.2s',
                        outline:'none',
                        background:'white'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display:'block',
                      fontSize:'0.85rem',
                      fontWeight:'600',
                      color:'#475569',
                      marginBottom:'8px'
                    }}>
                      Téléphone
                    </label>
                    <input
                      type="text"
                      placeholder="02 43 ..."
                      value={newSupplier.phone}
                      onChange={e => setNewSupplier({...newSupplier, phone: e.target.value})}
                      style={{
                        width:'100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        fontSize:'1rem',
                        transition:'all 0.2s',
                        outline:'none',
                        background:'white'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display:'block',
                      fontSize:'0.85rem',
                      fontWeight:'600',
                      color:'#475569',
                      marginBottom:'8px'
                    }}>
                      Catégorie
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Peinture, Elec..."
                      value={newSupplier.category}
                      onChange={e => setNewSupplier({...newSupplier, category: e.target.value})}
                      style={{
                        width:'100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        fontSize:'1rem',
                        transition:'all 0.2s',
                        outline:'none',
                        background:'white'
                      }}
                    />
                  </div>
                  <div style={{ display:'flex', alignItems:'end' }}>
                    <button
                      onClick={addSupplier}
                      style={{
                        width:'100%',
                        padding:'14px 20px',
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize:'1rem',
                        boxShadow:'0 6px 14px rgba(37,99,235,0.3)',
                        transition:'all 0.2s ease',
                        display:'flex',
                        alignItems:'center',
                        justifyContent:'center',
                        gap:'8px'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(37,99,235,0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 6px 14px rgba(37,99,235,0.3)';
                      }}
                    >
                      <Plus size={20} /> Ajouter
                    </button>
                  </div>
                </div>
              </div>

              {/* Liste Fournisseurs Modernisée */}
              <div style={{
                background: 'white',
                borderRadius: '24px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                boxShadow:'0 10px 25px -5px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03)'
              }}>
                {/* En-tête de tableau */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  padding: '18px 24px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'grid',
                  gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 80px',
                  gap: '16px',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize:'0.8rem', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>Nom</span>
                  <span style={{ fontSize:'0.8rem', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>Contact</span>
                  <span style={{ fontSize:'0.8rem', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>Téléphone</span>
                  <span style={{ fontSize:'0.8rem', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>Catégorie</span>
                  <span style={{ fontSize:'0.8rem', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', textAlign:'right' }}>Action</span>
                </div>

                {/* Lignes du tableau */}
                {suppliers.length === 0 ? (
                  <div style={{
                    padding:'60px 40px',
                    textAlign:'center',
                    color:'#94a3b8'
                  }}>
                    <Truck size={40} style={{ opacity:0.3, marginBottom:'16px' }} />
                    <p style={{ margin:0, fontWeight:'500' }}>Aucun fournisseur enregistré</p>
                  </div>
                ) : (
                  suppliers.map((s, index) => (
                    <div
                      key={s.id}
                      style={{
                        padding: '20px 24px',
                        display: 'grid',
                        gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 80px',
                        gap: '16px',
                        alignItems: 'center',
                        borderBottom: index < suppliers.length - 1 ? '1px solid #f1f5f9' : 'none',
                        transition: 'all 0.2s ease',
                        background: 'white'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#fafbfc';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <div style={{
                        fontWeight: '700',
                        color:'#1e293b',
                        fontSize:'1rem',
                        display:'flex',
                        alignItems:'center',
                        gap:'10px'
                      }}>
                        <div style={{
                          width:'40px',
                          height:'40px',
                          background:'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                          borderRadius:'10px',
                          display:'flex',
                          alignItems:'center',
                          justifyContent:'center',
                          color:'#64748b'
                        }}>
                          <Truck size={18} />
                        </div>
                        {s.name}
                      </div>
                      <div style={{ color:'#475569', fontWeight:'500' }}>{s.contact_name || '—'}</div>
                      <div style={{
                        color:'#475569',
                        fontFamily:'monospace',
                        background:'#f8fafc',
                        padding:'6px 12px',
                        borderRadius:'8px',
                        fontSize:'0.9rem',
                        display:'inline-block'
                      }}>
                        {s.phone || '—'}
                      </div>
                      <div>
                        <span style={{
                          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                          color:'#2563eb',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight:'600',
                          border:'1px solid #bfdbfe'
                        }}>
                          {s.category || 'Autre'}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <button
                          onClick={() => deleteSupplier(s.id)}
                          style={{
                            color: '#ef4444',
                            background: 'linear-gradient(145deg, #ffffff, #fef2f2)',
                            padding:'12px',
                            borderRadius:'12px',
                            border: '1px solid #fecaca',
                            cursor: 'pointer',
                            transition:'all 0.2s ease',
                            display:'flex',
                            alignItems:'center',
                            justifyContent:'center'
                          }}
                          title="Supprimer"
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(145deg, #ffffff, #fef2f2)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}